import React from 'react';

const Privacy: React.FC = () => {
  return (
    <div className="pt-48 pb-40 md:pt-48 md:pb-48">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-4">
            <span className="text-white">Privacy</span>
            <span className="block mt-4 bg-gradient-to-r from-primary to-[#E6C45C] text-transparent bg-clip-text">Policy</span>
          </h1>
          <p className="text-[#FFFFFFCC] mt-6">
            Last updated: {new Date().toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-lg prose-invert max-w-none">
          {/* Introduction */}
          <section className="mb-12">
            <p className="text-[#FFFFFFCC] leading-relaxed">
              Digital Memorials ("we," "our," or "us") is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website digitalmemorials.ie and use our services. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the site.
            </p>
          </section>

          {/* Data Controller Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Data Controller Information</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              Digital Memorials operates as a data controller and is responsible for your personal data. We are registered in Ireland, and our registered office is located in Waterford, Ireland. For all data protection matters, you can contact us at mail@digitalmemorials.ie.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Information We Collect</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed mb-4">
              We collect personal data that you voluntarily provide to us when you:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#FFFFFFCC]">
              <li>Register for our services</li>
              <li>Create a Digital Memorial</li>
              <li>Contact us through our contact form</li>
              <li>Subscribe to our newsletter</li>
              <li>Participate in creating or contributing to a memorial</li>
            </ul>
            
            <p className="text-[#FFFFFFCC] leading-relaxed mt-4">
              The personal data we collect may include:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#FFFFFFCC]">
              <li>Name and contact information (email address, phone number)</li>
              <li>Memorial content (photos, videos, written tributes)</li>
              <li>Payment information (processed securely through our payment providers)</li>
              <li>Technical data (IP address, browser type, device information)</li>
              <li>Usage data (how you interact with our website)</li>
            </ul>
          </section>

          {/* Legal Basis for Processing */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Legal Basis for Processing</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed mb-4">
              Under the General Data Protection Regulation (GDPR), we process your personal data based on the following legal grounds:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#FFFFFFCC]">
              <li>Consent: When you explicitly agree to the processing of your personal data</li>
              <li>Contract: When processing is necessary for the performance of a contract with you</li>
              <li>Legal Obligation: When we need to comply with a legal obligation</li>
              <li>Legitimate Interests: When processing is necessary for our legitimate interests or those of a third party</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">How We Use Your Information</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed mb-4">
              We use your personal data for the following purposes:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#FFFFFFCC]">
              <li>To provide and maintain our services</li>
              <li>To process and complete transactions</li>
              <li>To send administrative information</li>
              <li>To respond to inquiries and provide customer support</li>
              <li>To send marketing and promotional communications (with your consent)</li>
              <li>To improve our website and services</li>
              <li>To protect our legal rights and prevent misuse</li>
            </ul>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Data Retention</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including for the purposes of satisfying any legal, accounting, or reporting requirements. Memorial content is retained for the duration specified in your service agreement. You can request deletion of your personal data at any time by contacting us.
            </p>
          </section>

          {/* Data Security */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Data Security</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized or unlawful processing, accidental loss, destruction, or damage. These measures include encryption, secure servers, and regular security assessments. However, please note that no method of transmission over the Internet or electronic storage is 100% secure.
            </p>
          </section>

          {/* Your Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Your Rights Under GDPR</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed mb-4">
              Under the GDPR, you have the following rights:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#FFFFFFCC]">
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>
            <p className="text-[#FFFFFFCC] leading-relaxed mt-4">
              To exercise any of these rights, please contact us at mail@digitalmemorials.ie. We will respond to your request within one month.
            </p>
          </section>

          {/* Cookies */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Cookies and Tracking Technologies</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              We use cookies and similar tracking technologies to track activity on our website and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our service. We use both session cookies and persistent cookies.
            </p>
          </section>

          {/* Third-Party Services */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Third-Party Services</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              We may employ third-party companies and individuals to facilitate our service, provide service on our behalf, perform service-related services, or assist us in analyzing how our service is used. These third parties have access to your personal data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.
            </p>
          </section>

          {/* International Transfers */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">International Data Transfers</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those in your jurisdiction. If you are located outside Ireland and choose to provide information to us, please note that we transfer the data to Ireland and process it there. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Children's Privacy</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              Our service is not intended for use by children under the age of 16 without parental consent. We do not knowingly collect personal data from children under 16. If you become aware that a child has provided us with personal data without parental consent, please contact us. If we become aware that we have collected personal data from children without verification of parental consent, we take steps to remove that information from our servers.
            </p>
          </section>

          {/* Changes to Privacy Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Changes to This Privacy Policy</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          {/* Contact Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">Contact Us</h2>
            <p className="text-[#FFFFFFCC] leading-relaxed">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="mt-4 text-[#FFFFFFCC]">
              <p>Digital Memorials</p>
              <p>Email: mail@digitalmemorials.ie</p>
              <p>Data Protection Officer: dean@digitalmemorials.ie</p>
              <p className="mt-4">
                You also have the right to lodge a complaint with the Irish Data Protection Commission (www.dataprotection.ie) if you believe we have not handled your personal data in accordance with applicable law.
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy; 